import * as React from 'react';
import { IUseBvtFlagsCheckerProps } from '../types';
import useBvtFlagsChecker from '../hooks/use-bvt-flags-checker';

/**
 * Higher-Order Component (HOC) that conditionally renders a component based on a BVT flag,
 * supporting both functional and class components with type safety.
 *
 * @param {ComponentType<P>} WrappedComponent - The component to be wrapped.
 * @param {ComponentType<Q>} FallbackComponent - The component to be rendered as a fallback.
 * @param {IUseBvtFlagsCheckerProps} bvtFlagProps - Props for the useBvtFlagsChecker hook.
 * @returns {FC<P & Q>} A new component that conditionally renders the WrappedComponent or the FallbackComponent.
 */
const withBvtFlagChecker = <P extends object, Q extends object>(
    WrappedComponent: React.ComponentType<P>,
    FallbackComponent: React.ComponentType<Q>,
    bvtFlagProps: IUseBvtFlagsCheckerProps
): React.FC<P & Q> => {
  type CombinedProps = P & Q;

  const WithBvtFlagComponent: React.FC<CombinedProps> = (props: CombinedProps) => {
      const { isBvtFlagEnabled } = useBvtFlagsChecker(bvtFlagProps);

      if (isBvtFlagEnabled) {
          return <WrappedComponent {...props} />;
      }

      return <FallbackComponent {...props} />;
  };

  WithBvtFlagComponent.displayName = `withBvtFlag(${
      WrappedComponent?.displayName || WrappedComponent?.name || 'Component'
  })`;

  return WithBvtFlagComponent;
};

export default withBvtFlagChecker;
